import { useState, useRef, useEffect, useCallback, createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import { SandboxDb } from '../../lib/sandbox-db';
import SitePreviewRenderer from '../../components/onboarding/SitePreviewRenderer';
// site-schema types available via SandboxDb

// ─── Types ───────────────────────────────────────────────────────────────────

type Mode = 'concierge' | 'studio' | 'canvas';
type StudioTab = 'chat' | 'content' | 'design' | 'settings';
type Viewport = 'mobile' | 'desktop' | 'wide';
type Tier = 'sandbox' | 'free' | 'pro';
type ClaimStep = 'choose' | 'subdomain' | 'domain' | 'complete';

interface Message {
  id: number;
  role: 'ai' | 'user' | 'system';
  text: string;
  options?: { label: string; value: string }[];
  actions?: { label: string; icon: string; value: string }[];
}

interface Site {
  name: string;
  initial: string;
  status: 'live' | 'draft' | 'building';
  url: string;
  version: number;
  tier: Tier;
}

interface Suggestion {
  id: number;
  text: string;
  icon: string;
  applied: boolean;
}

const SandboxContext = createContext<SandboxDb | null>(null);
export const useSandbox = () => useContext(SandboxContext);

// ─── Constants ───────────────────────────────────────────────────────────────

const EXISTING_SITES: Site[] = [
  { name: 'Fresh Bakery', initial: 'FB', status: 'live', url: 'fresh-bakery.wiserwebhost.com', version: 3, tier: 'free' },
  { name: 'My Portfolio', initial: 'MP', status: 'draft', url: 'sarah-dev.wiserwebhost.com', version: 1, tier: 'sandbox' },
];

const INITIAL_SUGGESTIONS: Suggestion[] = [
  { id: 1, text: 'Add customer reviews to build trust', icon: 'star', applied: false },
  { id: 2, text: 'Compress images — save 40% bandwidth', icon: 'speed', applied: false },
  { id: 3, text: 'Add opening hours to the homepage', icon: 'schedule', applied: false },
];

const PERKS = [
  { icon: 'shield', label: 'DDoS Protection', desc: 'Enterprise-grade, always on' },
  { icon: 'hub', label: 'Clustered Hosting', desc: 'Multi-server redundancy' },
  { icon: 'all_inclusive', label: 'Unlimited Bandwidth', desc: 'No traffic caps, ever' },
  { icon: 'speed', label: 'Free Global CDN', desc: 'S3 + nginx edge caching' },
  { icon: 'https', label: 'Free SSL', desc: 'Auto-provisioned HTTPS' },
  { icon: 'monitoring', label: 'Monitoring & Analytics', desc: 'Real-time visitor stats' },
  { icon: 'smart_toy', label: 'AI Development', desc: 'AI builds & maintains your site' },
  { icon: 'dns', label: 'Premium DNS', desc: 'WiserWebDNS global anycast' },
];

let msgId = 0;
const mkMsg = (
  role: Message['role'],
  text: string,
  opts?: { options?: Message['options']; actions?: Message['actions'] },
): Message => ({ id: ++msgId, role, text, options: opts?.options, actions: opts?.actions });

// ─── Component ───────────────────────────────────────────────────────────────

export default function UnifiedView() {
  // Core state
  const [mode, setMode] = useState<Mode>('concierge');
  const [sites, setSites] = useState<Site[]>(EXISTING_SITES);
  const [activeSite, setActiveSite] = useState<Site | null>(null);
  const [siteRailOpen, setSiteRailOpen] = useState(true);

  // Chat state
  const [messages, setMessages] = useState<Message[]>([
    mkMsg('ai', "Hi! I'm your WiserWeb assistant. What would you like to do?", {
      options: [
        { label: 'Build a new site', value: 'new' },
        { label: 'Migrate an existing site', value: 'migrate' },
        { label: 'Freshen up a site', value: 'facelift' },
      ],
    }),
  ]);
  const [chatInput, setChatInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [onboardStep, setOnboardStep] = useState(0);

  // Studio state
  const [studioTab, setStudioTab] = useState<StudioTab>('chat');
  const [viewport, setViewport] = useState<Viewport>('desktop');

  // Canvas state
  const [suggestions, setSuggestions] = useState<Suggestion[]>(INITIAL_SUGGESTIONS);
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);

  // Conversion funnel state
  const [showClaimOverlay, setShowClaimOverlay] = useState(false);
  const [claimStep, setClaimStep] = useState<ClaimStep>('choose');
  const [subdomainInput, setSubdomainInput] = useState('');
  const [domainInput, setDomainInput] = useState('');
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(null);

  // Refs
  const chatEndRef = useRef<HTMLDivElement>(null);
  const sandboxDbRef = useRef<SandboxDb | null>(null);
  const [, setSandboxReady] = useState(false);

  useEffect(() => {
    const db = new SandboxDb();
    sandboxDbRef.current = db;
    db.init().then(() => {
      setSandboxReady(true);
    }).catch((err) => {
      console.error('SandboxDb init failed:', err);
    });
    return () => {
      db.close();
    };
  }, []);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
  }, []);

  useEffect(scrollToBottom, [messages, typing, scrollToBottom]);

  // ─── Chat helpers ────────────────────────────────────────────────────────

  const addAiMsg = useCallback(
    (text: string, opts?: { options?: Message['options']; actions?: Message['actions'] }, delay = 1200) => {
      setTyping(true);
      setTimeout(() => {
        setTyping(false);
        setMessages((prev) => [...prev, mkMsg('ai', text, opts)]);
      }, delay);
    },
    [],
  );

  const addSystemMsg = useCallback((text: string) => {
    setMessages((prev) => [...prev, mkMsg('system', text)]);
  }, []);

  // ─── Transition helpers ──────────────────────────────────────────────────

  const transitionToStudio = useCallback((site: Site) => {
    setActiveSite(site);
    setMode('studio');
    setStudioTab('chat');
    setChatDrawerOpen(false);
  }, []);

  const transitionToCanvas = useCallback((site: Site) => {
    setActiveSite(site);
    setMode('canvas');
    setChatDrawerOpen(false);
    setSuggestions(INITIAL_SUGGESTIONS.map((s) => ({ ...s, applied: false })));
  }, []);

  const startNewSite = useCallback(() => {
    setMode('concierge');
    setActiveSite(null);
    setOnboardStep(0);
    setMessages([
      mkMsg('ai', "Let's build something! What would you like to create?", {
        options: [
          { label: 'Build a new site', value: 'new' },
          { label: 'Migrate an existing site', value: 'migrate' },
          { label: 'Freshen up a site', value: 'facelift' },
        ],
      }),
    ]);
  }, []);

  // ─── Build sequence (concierge → claim overlay → studio) ──────────────

  const runBuildSequence = useCallback(
    (siteName: string, templateSlug = 'restaurant') => {
      addAiMsg("Building your site now... this'll just take a moment.", undefined, 600);

      const slug = siteName.toLowerCase().replace(/\s+/g, '-');
      const newSite: Site = {
        name: siteName,
        initial: siteName.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase(),
        status: 'building',
        url: `${slug}.wiserwebhost.com`,
        version: 1,
        tier: 'sandbox',
      };

      setSubdomainInput(slug);

      // Load template into SandboxDb
      const db = sandboxDbRef.current;
      if (db?.ready) {
        db.loadTemplate(templateSlug).then(async () => {
          // Update site name in the template
          await db.updateSiteMeta({ name: siteName } as any);
        }).catch((err) => {
          console.error('Template load failed:', err);
        });
      }

      setTimeout(() => addSystemMsg('Backup saved: v1 — initial generation'), 1800);

      setTimeout(() => {
        newSite.status = 'live';
        setSites((prev) => [newSite, ...prev]);
        setActiveSite(newSite);
        setMode('studio');
        setStudioTab('chat');

        addAiMsg(
          `Your sandbox site is live! Claim your URL to keep it permanently.`,
          {
            actions: [
              { label: 'Claim my site', icon: 'verified', value: 'claim' },
              { label: 'Keep exploring first', icon: 'explore', value: 'explore' },
            ],
          },
          600,
        );
      }, 3000);
    },
    [addAiMsg, addSystemMsg],
  );

  // ─── Claim flow helpers ──────────────────────────────────────────────────

  const checkSubdomain = () => {
    if (!subdomainInput.trim()) return;
    // Simulate availability check
    setTimeout(() => {
      setSubdomainAvailable(!['admin', 'www', 'api', 'dashboard', 'test'].includes(subdomainInput.toLowerCase()));
    }, 500);
  };

  const completeClaim = (tier: Tier) => {
    setClaimStep('complete');
    if (activeSite) {
      const url = tier === 'pro' && domainInput
        ? domainInput
        : `${subdomainInput}.wiserwebhost.com`;
      const updated = { ...activeSite, tier, url };
      setActiveSite(updated);
      setSites((prev) => prev.map((s) => (s.name === activeSite.name ? updated : s)));
    }
    // Auto-dismiss after a moment
    setTimeout(() => {
      setShowClaimOverlay(false);
      setClaimStep('choose');
      if (activeSite) {
        addAiMsg(
          "Your site is claimed and secure! Here are some suggestions to make it even better:",
          {
            actions: [
              { label: 'Add testimonials', icon: 'star', value: 'testimonials' },
              { label: 'Improve hero section', icon: 'palette', value: 'contrast' },
              { label: 'Speed up images', icon: 'speed', value: 'images' },
            ],
          },
        );
      }
    }, 1800);
  };

  // ─── Chat interaction handler ────────────────────────────────────────────

  const handleOption = (value: string, label: string) => {
    setMessages((prev) => {
      const updated = [...prev];
      const last = updated[updated.length - 1];
      if (last.role === 'ai') {
        updated[updated.length - 1] = { ...last, options: undefined, actions: undefined };
      }
      return [...updated, mkMsg('user', label)];
    });

    // Handle claim action from chat
    if (value === 'claim') {
      setShowClaimOverlay(true);
      setClaimStep('choose');
      return;
    }
    if (value === 'explore') {
      addAiMsg("No rush! Your sandbox is safe for now. When you're ready, click the \"Claim\" badge in the header to secure your URL.", undefined, 600);
      return;
    }

    if (mode === 'concierge') {
      if (onboardStep === 0) {
        setOnboardStep(1);
        if (value === 'new') {
          addAiMsg("Tell me about your new site. What's it for? A business, portfolio, store, blog...?");
        } else if (value === 'migrate') {
          addAiMsg("Paste the URL of the site you'd like to move to WiserWebCloud. I'll analyze it and rebuild it here.");
        } else {
          addAiMsg('Which site needs a refresh?', {
            options: sites.map((s) => ({ label: s.name, value: s.name })),
          });
        }
      } else if (onboardStep === 1) {
        setOnboardStep(2);
        addAiMsg('Do you have branding (logo, colors) or would you like me to create some?', {
          options: [
            { label: 'I have branding', value: 'has_brand' },
            { label: 'Create branding for me', value: 'need_brand' },
            { label: 'Keep it simple for now', value: 'skip_brand' },
          ],
        });
      } else if (onboardStep === 2) {
        setOnboardStep(3);
        if (value === 'need_brand') {
          addAiMsg('What vibe are you going for?', {
            options: [
              { label: 'Modern & minimal', value: 'modern' },
              { label: 'Warm & friendly', value: 'warm' },
              { label: 'Bold & vibrant', value: 'bold' },
            ],
          });
        } else {
          runBuildSequence('Fresh Bakery');
        }
      } else if (onboardStep === 3) {
        runBuildSequence('Fresh Bakery');
      }
    }

    // Studio/canvas action handling
    if (mode === 'studio' || mode === 'canvas') {
      addSystemMsg(`Backup saved: v${(activeSite?.version || 1) + 1}`);
      if (activeSite) {
        setActiveSite({ ...activeSite, version: activeSite.version + 1 });
      }
      addAiMsg(`Done! I've applied "${label}" and updated the preview. Want me to change anything else?`, {
        actions: [
          { label: 'Undo this change', icon: 'undo', value: 'undo' },
          { label: 'Looks great!', icon: 'check', value: 'done' },
        ],
      });
    }
  };

  const handleSend = () => {
    if (!chatInput.trim()) return;
    const text = chatInput.trim();
    setChatInput('');
    setMessages((prev) => [...prev, mkMsg('user', text)]);

    if (mode === 'concierge' && onboardStep >= 1) {
      setOnboardStep(2);
      addAiMsg('Do you have branding (logo, colors) or would you like me to create some?', {
        options: [
          { label: 'I have branding', value: 'has_brand' },
          { label: 'Create branding for me', value: 'need_brand' },
          { label: 'Keep it simple for now', value: 'skip_brand' },
        ],
      });
    } else if (mode === 'studio' || mode === 'canvas') {
      addSystemMsg(`Backup saved: v${(activeSite?.version || 1) + 1}`);
      if (activeSite) {
        setActiveSite({ ...activeSite, version: activeSite.version + 1 });
      }
      addAiMsg("Working on it! I saved a backup first, then applied your changes. Check the preview to see the update.");
    }
  };

  // ─── Tier badge helper ───────────────────────────────────────────────────

  const renderTierBadge = (site: Site | null, clickable = false) => {
    if (!site) return null;
    const cfg = {
      sandbox: { label: 'Sandbox', cls: 'bg-amber-500/15 border-amber-500/30 text-amber-400', icon: 'hourglass_empty' },
      free: { label: 'Free', cls: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400', icon: 'check_circle' },
      pro: { label: 'Pro', cls: 'bg-purple-500/15 border-purple-500/30 text-purple-400', icon: 'workspace_premium' },
    }[site.tier];

    const badge = (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded-full text-[10px] font-medium ${cfg.cls} ${clickable ? 'cursor-pointer hover:opacity-80 transition' : ''}`}>
        <span className="material-icons text-[10px]">{cfg.icon}</span>
        {cfg.label}
        {site.tier === 'sandbox' && clickable && <span className="material-icons text-[10px] ml-0.5">arrow_drop_down</span>}
      </span>
    );

    if (clickable && site.tier === 'sandbox') {
      return (
        <button onClick={() => { setShowClaimOverlay(true); setClaimStep('choose'); }}>
          {badge}
        </button>
      );
    }
    if (clickable && site.tier === 'free') {
      return (
        <button onClick={() => { setShowClaimOverlay(true); setClaimStep('choose'); }}>
          {badge}
        </button>
      );
    }
    return badge;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // CLAIM OVERLAY — conversion funnel
  // ═══════════════════════════════════════════════════════════════════════════

  const renderClaimOverlay = () => {
    if (!showClaimOverlay) return null;

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowClaimOverlay(false)} />

        {/* Modal */}
        <div className="relative bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          {/* Close */}
          <button
            onClick={() => setShowClaimOverlay(false)}
            className="absolute top-3 right-3 text-zinc-500 hover:text-zinc-300 transition z-10"
          >
            <span className="material-icons text-lg">close</span>
          </button>

          {claimStep === 'choose' && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-2">
                  {activeSite?.tier === 'sandbox' ? 'Claim Your Site' : 'Upgrade Your Plan'}
                </h2>
                <p className="text-sm text-zinc-400">
                  {activeSite?.tier === 'sandbox'
                    ? 'Your sandbox site is ready. Secure your URL to keep it permanently.'
                    : 'Unlock custom domains and priority support.'}
                </p>
              </div>

              {/* Tier cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {/* Free tier */}
                <button
                  onClick={() => setClaimStep('subdomain')}
                  className="group text-left p-5 bg-zinc-800/60 border-2 border-zinc-700 rounded-xl hover:border-emerald-500/50 transition-all"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-lg font-bold text-white">Free</span>
                    <span className="text-2xl font-bold text-emerald-400">$0</span>
                  </div>
                  <p className="text-xs text-zinc-400 mb-4">
                    Host on our domain — perfect for personal projects, portfolios, and testing.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs text-zinc-300">
                      <span className="material-icons text-emerald-400 text-sm">check</span>
                      yourname.wiserwebhost.com subdomain
                    </div>
                    <div className="flex items-center gap-2 text-xs text-zinc-300">
                      <span className="material-icons text-emerald-400 text-sm">check</span>
                      All 8 included features below
                    </div>
                    <div className="flex items-center gap-2 text-xs text-zinc-300">
                      <span className="material-icons text-emerald-400 text-sm">check</span>
                      AI-assisted site building
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <span className="px-4 py-2 bg-emerald-500/15 border border-emerald-500/30 rounded-lg text-sm font-medium text-emerald-400 group-hover:bg-emerald-500/25 transition inline-block">
                      Claim Free Subdomain
                    </span>
                  </div>
                </button>

                {/* Pro tier */}
                <button
                  onClick={() => setClaimStep('domain')}
                  className="group text-left p-5 bg-zinc-800/60 border-2 border-orange-500/30 rounded-xl hover:border-orange-500/60 transition-all relative"
                >
                  <span className="absolute -top-2.5 left-4 px-2 py-0.5 bg-orange-500 text-white text-[9px] font-bold rounded uppercase">
                    Most Popular
                  </span>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-lg font-bold text-white">Pro</span>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-orange-400">$15</span>
                      <span className="text-xs text-zinc-500">/mo</span>
                    </div>
                  </div>
                  <p className="text-xs text-zinc-400 mb-4">
                    Bring your own domain — ideal for businesses, client work, and production sites.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs text-zinc-300">
                      <span className="material-icons text-orange-400 text-sm">check</span>
                      Connect your own domain(s)
                    </div>
                    <div className="flex items-center gap-2 text-xs text-zinc-300">
                      <span className="material-icons text-orange-400 text-sm">check</span>
                      Everything in Free, plus:
                    </div>
                    <div className="flex items-center gap-2 text-xs text-zinc-300">
                      <span className="material-icons text-orange-400 text-sm">check</span>
                      Priority AI assistance & support
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <span className="px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg text-sm font-medium text-white group-hover:shadow-glow transition inline-block">
                      Start 14-Day Free Trial
                    </span>
                  </div>
                </button>
              </div>

              {/* Included with every plan */}
              <div className="border-t border-zinc-800 pt-6">
                <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-4 text-center">
                  Included free with every site
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {PERKS.map((perk) => (
                    <div key={perk.label} className="flex items-start gap-2 p-2.5 bg-zinc-800/30 rounded-lg">
                      <span className="material-icons text-orange-400 text-base mt-0.5 shrink-0">{perk.icon}</span>
                      <div>
                        <p className="text-xs font-medium text-zinc-300">{perk.label}</p>
                        <p className="text-[10px] text-zinc-600 mt-0.5">{perk.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-center text-[10px] text-zinc-600 mt-3">
                  Features that other providers charge extra for. We include them all.
                </p>
              </div>
            </div>
          )}

          {claimStep === 'subdomain' && (
            <div className="p-6 md:p-8">
              <button onClick={() => setClaimStep('choose')} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition mb-6">
                <span className="material-icons text-sm">arrow_back</span> Back
              </button>

              <h2 className="text-xl font-bold text-white mb-2">Pick Your Subdomain</h2>
              <p className="text-sm text-zinc-400 mb-6">This is your site's permanent address. Choose something memorable.</p>

              <div className="flex items-stretch gap-2 mb-3">
                <div className="flex-1 flex items-center bg-zinc-800/60 border border-zinc-700 rounded-xl overflow-hidden">
                  <input
                    type="text"
                    value={subdomainInput}
                    onChange={(e) => {
                      setSubdomainInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                      setSubdomainAvailable(null);
                    }}
                    placeholder="your-site-name"
                    className="flex-1 bg-transparent px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none font-mono"
                  />
                  <span className="pr-3 text-xs text-zinc-500 font-mono shrink-0">.wiserwebhost.com</span>
                </div>
                <button
                  onClick={checkSubdomain}
                  className="px-4 bg-zinc-800 border border-zinc-700 rounded-xl text-sm text-zinc-300 hover:bg-zinc-700 transition"
                >
                  Check
                </button>
              </div>

              {subdomainAvailable === true && (
                <div className="flex items-center gap-2 mb-6 text-emerald-400 animate-fade-in">
                  <span className="material-icons text-sm">check_circle</span>
                  <span className="text-sm font-medium">{subdomainInput}.wiserwebhost.com is available!</span>
                </div>
              )}
              {subdomainAvailable === false && (
                <div className="flex items-center gap-2 mb-6 text-red-400 animate-fade-in">
                  <span className="material-icons text-sm">cancel</span>
                  <span className="text-sm">That subdomain is taken. Try another.</span>
                </div>
              )}

              {subdomainAvailable === true && (
                <button
                  onClick={() => completeClaim('free')}
                  className="w-full py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold rounded-xl hover:shadow-lg transition animate-fade-in"
                >
                  Claim {subdomainInput}.wiserwebhost.com — Free Forever
                </button>
              )}

              <p className="text-center text-xs text-zinc-600 mt-4">
                Want your own domain instead?{' '}
                <button onClick={() => setClaimStep('domain')} className="text-orange-400 hover:text-orange-300 transition">
                  Upgrade to Pro ($15/mo)
                </button>
              </p>
            </div>
          )}

          {claimStep === 'domain' && (
            <div className="p-6 md:p-8">
              <button onClick={() => setClaimStep('choose')} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition mb-6">
                <span className="material-icons text-sm">arrow_back</span> Back
              </button>

              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-xl font-bold text-white">Connect Your Domain</h2>
                <span className="px-2 py-0.5 bg-orange-500/15 border border-orange-500/30 rounded text-[10px] text-orange-400 font-medium">Pro $15/mo</span>
              </div>
              <p className="text-sm text-zinc-400 mb-6">Point your domain to WiserWebCloud. We handle SSL, DNS, and CDN automatically.</p>

              <div className="bg-zinc-800/60 border border-zinc-700 rounded-xl p-4 mb-4">
                <label className="text-xs text-zinc-500 mb-2 block">Your domain</label>
                <input
                  type="text"
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  placeholder="www.mybusiness.com"
                  className="w-full bg-zinc-900/60 border border-zinc-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none font-mono"
                />
              </div>

              <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-4 mb-6">
                <p className="text-xs text-zinc-500 mb-2 font-medium">What you get with Pro:</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    'Custom domain(s)',
                    'Priority AI support',
                    'Advanced analytics',
                    'Staging environments',
                    'Team collaboration',
                    'All 8 free perks',
                  ].map((f) => (
                    <div key={f} className="flex items-center gap-1.5 text-xs text-zinc-300">
                      <span className="material-icons text-orange-400 text-xs">check</span>
                      {f}
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => domainInput.trim() && completeClaim('pro')}
                disabled={!domainInput.trim()}
                className="w-full py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:shadow-glow transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Start 14-Day Free Trial
              </button>
              <p className="text-center text-[10px] text-zinc-600 mt-2">No credit card required. Cancel anytime.</p>

              <p className="text-center text-xs text-zinc-600 mt-4">
                Just need a free subdomain?{' '}
                <button onClick={() => setClaimStep('subdomain')} className="text-emerald-400 hover:text-emerald-300 transition">
                  Claim free subdomain
                </button>
              </p>
            </div>
          )}

          {claimStep === 'complete' && (
            <div className="p-8 md:p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 rounded-full flex items-center justify-center">
                <span className="material-icons text-3xl text-emerald-400">celebration</span>
              </div>
              <h2 className="text-xl font-bold text-white mb-2">
                {activeSite?.tier === 'pro' ? 'Welcome to Pro!' : 'Site Claimed!'}
              </h2>
              <p className="text-sm text-zinc-400 mb-1">Your site is secured at:</p>
              <p className="text-base font-mono text-orange-400 mb-4">{activeSite?.url}</p>
              <div className="flex flex-wrap justify-center gap-2">
                {PERKS.slice(0, 4).map((p) => (
                  <span key={p.label} className="px-2 py-1 bg-zinc-800/60 border border-zinc-700/50 rounded-full text-[10px] text-zinc-400 flex items-center gap-1">
                    <span className="material-icons text-orange-400 text-[10px]">{p.icon}</span>
                    {p.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── Shared: chat panel ──────────────────────────────────────────────────

  const renderChat = (compact = false) => (
    <div className={`flex flex-col ${compact ? 'h-full' : 'flex-1'}`}>
      <div className={`flex-1 overflow-y-auto px-4 py-4 space-y-3 ${compact ? 'min-h-0' : ''}`}>
        {messages.map((m) => {
          if (m.role === 'system') {
            return (
              <div key={m.id} className="flex justify-center">
                <span className="text-[10px] text-zinc-600 bg-zinc-800/40 px-2.5 py-0.5 rounded-full">{m.text}</span>
              </div>
            );
          }
          return (
            <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${
                  m.role === 'user'
                    ? 'bg-orange-500/15 border border-orange-500/30 rounded-br-md'
                    : 'bg-zinc-800/60 border border-zinc-700/50 rounded-bl-md'
                }`}
              >
                {m.role === 'ai' && (
                  <span className="material-icons text-orange-400 text-xs mr-1 align-middle">smart_toy</span>
                )}
                <span className={`text-sm whitespace-pre-line ${m.role === 'user' ? 'text-orange-200' : 'text-zinc-300'}`}>
                  {m.text}
                </span>
                {m.options && (
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {m.options.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => handleOption(opt.value, opt.label)}
                        className="px-3 py-1.5 bg-zinc-700/50 hover:bg-orange-500/20 border border-zinc-600 hover:border-orange-500/50 rounded-lg text-xs text-zinc-300 hover:text-orange-300 transition"
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
                {m.actions && (
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {m.actions.map((a) => (
                      <button
                        key={a.value}
                        onClick={() => handleOption(a.value, a.label)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-zinc-700/50 hover:bg-orange-500/20 border border-zinc-600 hover:border-orange-500/50 rounded-lg text-[11px] text-zinc-300 hover:text-orange-300 transition"
                      >
                        <span className="material-icons text-xs">{a.icon}</span>
                        {a.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {typing && (
          <div className="flex justify-start">
            <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" />
                <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      <div className="p-3 border-t border-zinc-800 shrink-0">
        <div className="flex items-center gap-2 bg-zinc-800/60 border border-zinc-700/50 rounded-xl px-3">
          <input
            type="text"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder={mode === 'concierge' ? 'Describe your site...' : 'Ask anything about your site...'}
            className="flex-1 bg-transparent py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none"
          />
          <button onClick={handleSend} className="text-orange-400 hover:text-orange-300 transition p-0.5">
            <span className="material-icons text-lg">send</span>
          </button>
        </div>
      </div>
    </div>
  );

  // ─── Shared: site preview ────────────────────────────────────────────────

  const renderSitePreview = (size: 'compact' | 'large' = 'compact') => {
    return (
      <SitePreviewRenderer
        sandboxDb={sandboxDbRef.current}
        viewport={viewport}
        size={size}
        siteUrl={activeSite?.url}
        tier={activeSite?.tier}
        onClaimClick={() => { setShowClaimOverlay(true); setClaimStep('choose'); }}
      />
    );
  };

  // ─── Sites rail ──────────────────────────────────────────────────────────

  const renderSiteRail = () => (
    <div className={`border-r border-zinc-800 flex flex-col bg-zinc-950/50 shrink-0 transition-all duration-300 ${siteRailOpen ? 'w-14 md:w-52' : 'w-0 overflow-hidden'}`}>
      <div className="h-12 flex items-center justify-center md:justify-start px-2 md:px-3 border-b border-zinc-800 shrink-0">
        <span className="text-orange-500 font-bold text-sm hidden md:block">WiserWeb</span>
        <span className="text-orange-500 font-bold text-sm md:hidden">W</span>
      </div>

      <div className="flex-1 overflow-y-auto py-1.5">
        <button onClick={startNewSite} className="w-full flex items-center gap-2 px-2 md:px-3 py-2 text-orange-400 hover:bg-zinc-800/50 transition text-sm">
          <span className="material-icons text-lg">add</span>
          <span className="hidden md:inline text-xs">New Site</span>
        </button>

        <div className="border-t border-zinc-800/50 mt-1 pt-1">
          {sites.map((site) => (
            <button
              key={site.name}
              onClick={() => {
                if (site.status === 'live' || site.status === 'draft') transitionToCanvas(site);
              }}
              className={`w-full flex items-center gap-2 px-2 md:px-3 py-2 transition text-xs ${
                activeSite?.name === site.name ? 'bg-orange-500/10 text-white border-l-2 border-orange-500' : 'text-zinc-400 hover:bg-zinc-800/50'
              }`}
            >
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500/30 to-red-500/30 flex items-center justify-center text-[10px] font-bold text-orange-400 shrink-0 relative">
                {site.initial}
                {site.status === 'live' && (
                  <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-emerald-500 rounded-full border border-zinc-950" />
                )}
              </div>
              <div className="hidden md:block text-left min-w-0">
                <p className="truncate">{site.name}</p>
                <p className="text-[9px] text-zinc-600">
                  {site.tier === 'sandbox' ? '◌ Sandbox' : site.tier === 'pro' ? '★ Pro' : site.status === 'live' ? '● Live' : '○ Draft'}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="p-2 border-t border-zinc-800 shrink-0">
        <Link to="/mockups" className="flex items-center gap-1.5 text-[10px] text-zinc-600 hover:text-zinc-400 transition px-1">
          <span className="material-icons text-xs">arrow_back</span>
          <span className="hidden md:inline">Gallery</span>
        </Link>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // MODE: CONCIERGE
  // ═══════════════════════════════════════════════════════════════════════════

  const renderConcierge = () => (
    <div className="flex-1 flex flex-col min-w-0">
      <div className="h-12 flex items-center justify-between px-4 md:px-6 border-b border-zinc-800 shrink-0">
        <div className="flex items-center gap-2">
          <button onClick={() => setSiteRailOpen(!siteRailOpen)} className="text-zinc-500 hover:text-zinc-300 transition md:hidden">
            <span className="material-icons text-lg">menu</span>
          </button>
          <h1 className="text-sm font-medium text-white">New Site</h1>
        </div>
        <span className="text-xs text-zinc-600">AI Concierge</span>
      </div>
      <div className="flex-1 flex justify-center min-h-0">
        <div className="w-full max-w-2xl flex flex-col min-h-0">
          {renderChat()}
        </div>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // MODE: STUDIO
  // ═══════════════════════════════════════════════════════════════════════════

  const renderStudio = () => {
    const previewWidths: Record<Viewport, string> = {
      mobile: 'max-w-[320px]',
      desktop: 'max-w-full',
      wide: 'max-w-full',
    };

    return (
      <div className="flex-1 flex min-h-0 min-w-0">
        <div className="w-full md:w-[360px] lg:w-[400px] border-r border-zinc-800 flex flex-col shrink-0 min-h-0">
          <div className="h-12 flex items-center justify-between px-3 border-b border-zinc-800 shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <button onClick={() => setSiteRailOpen(!siteRailOpen)} className="text-zinc-500 hover:text-zinc-300 transition md:hidden">
                <span className="material-icons text-lg">menu</span>
              </button>
              <span className="text-sm font-medium text-white truncate">{activeSite?.name}</span>
              {renderTierBadge(activeSite, true)}
            </div>
            <button
              onClick={() => activeSite && transitionToCanvas(activeSite)}
              className="text-[10px] text-zinc-500 hover:text-orange-400 transition flex items-center gap-0.5"
            >
              <span className="material-icons text-xs">grid_view</span>
              <span className="hidden lg:inline">Overview</span>
            </button>
          </div>

          <div className="flex border-b border-zinc-800 shrink-0">
            {([
              { id: 'chat' as const, icon: 'chat', label: 'Chat' },
              { id: 'content' as const, icon: 'edit_note', label: 'Content' },
              { id: 'design' as const, icon: 'palette', label: 'Design' },
              { id: 'settings' as const, icon: 'tune', label: 'Settings' },
            ]).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setStudioTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1 py-2 text-[11px] font-medium transition border-b-2 ${
                  studioTab === tab.id ? 'border-orange-500 text-orange-400' : 'border-transparent text-zinc-600 hover:text-zinc-400'
                }`}
              >
                <span className="material-icons text-sm">{tab.icon}</span>
                <span className="hidden lg:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          {studioTab === 'chat' ? (
            renderChat(true)
          ) : (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center">
                <span className="material-icons text-3xl text-zinc-700 mb-2 block">
                  {studioTab === 'content' ? 'edit_note' : studioTab === 'design' ? 'palette' : 'tune'}
                </span>
                <p className="text-xs text-zinc-500">
                  {studioTab === 'content' && 'Edit text, images, and pages'}
                  {studioTab === 'design' && 'Colors, fonts, and layout'}
                  {studioTab === 'settings' && 'Domain, SEO, analytics'}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:flex flex-1 flex-col bg-zinc-950/30 min-w-0 min-h-0">
          <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800/50 shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-zinc-600">Preview</span>
              <span className="text-[10px] text-zinc-700">v{activeSite?.version || 1}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex gap-0.5 bg-zinc-800/50 rounded-lg p-0.5">
                {([
                  { id: 'mobile' as const, icon: 'phone_iphone' },
                  { id: 'desktop' as const, icon: 'laptop' },
                  { id: 'wide' as const, icon: 'monitor' },
                ]).map((vp) => (
                  <button
                    key={vp.id}
                    onClick={() => setViewport(vp.id)}
                    className={`p-1 rounded transition ${viewport === vp.id ? 'bg-zinc-700 text-orange-400' : 'text-zinc-600 hover:text-zinc-400'}`}
                  >
                    <span className="material-icons text-sm">{vp.icon}</span>
                  </button>
                ))}
              </div>
              <button className="px-3 py-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-[11px] font-semibold rounded-lg hover:shadow-glow transition">
                Publish
              </button>
            </div>
          </div>

          <div className="flex-1 flex items-start justify-center p-4 md:p-6 overflow-y-auto">
            <div className={`w-full ${previewWidths[viewport]} transition-all duration-300`}>
              {renderSitePreview('compact')}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // MODE: CANVAS
  // ═══════════════════════════════════════════════════════════════════════════

  const renderCanvas = () => (
    <div className="flex-1 flex flex-col min-w-0 min-h-0">
      <div className="h-12 flex items-center justify-between px-4 md:px-6 border-b border-zinc-800 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => setSiteRailOpen(!siteRailOpen)} className="text-zinc-500 hover:text-zinc-300 transition md:hidden">
            <span className="material-icons text-lg">menu</span>
          </button>
          <span className="text-sm font-medium text-white truncate">{activeSite?.name}</span>
          {renderTierBadge(activeSite, true)}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-zinc-600 hidden sm:inline">v{activeSite?.version} saved just now</span>
          <button className="text-zinc-500 hover:text-zinc-300 transition"><span className="material-icons text-lg">history</span></button>
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-[10px] font-bold text-white">S</div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">

          {/* Sandbox claim banner */}
          {activeSite?.tier === 'sandbox' && (
            <button
              onClick={() => { setShowClaimOverlay(true); setClaimStep('choose'); }}
              className="w-full mb-5 p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl flex items-center justify-between gap-3 hover:border-amber-500/50 transition group"
            >
              <div className="flex items-center gap-3">
                <span className="material-icons text-amber-400 text-xl">hourglass_empty</span>
                <div className="text-left">
                  <p className="text-sm font-medium text-white">This is a sandbox site</p>
                  <p className="text-xs text-zinc-400">Claim your URL to keep it permanently — it's free.</p>
                </div>
              </div>
              <span className="px-3 py-1.5 bg-amber-500 text-white text-xs font-semibold rounded-lg group-hover:bg-amber-400 transition shrink-0">
                Claim Now
              </span>
            </button>
          )}

          {/* Free tier upgrade nudge */}
          {activeSite?.tier === 'free' && (
            <button
              onClick={() => { setShowClaimOverlay(true); setClaimStep('domain'); }}
              className="w-full mb-5 p-3 bg-zinc-900/60 border border-zinc-800 rounded-xl flex items-center justify-between gap-3 hover:border-orange-500/30 transition group"
            >
              <div className="flex items-center gap-2 text-xs text-zinc-400">
                <span className="material-icons text-sm text-orange-400">workspace_premium</span>
                Want to use your own domain? Upgrade to Pro for $15/mo
              </div>
              <span className="text-xs text-orange-400 group-hover:text-orange-300 transition shrink-0">Upgrade</span>
            </button>
          )}

          {renderSitePreview('large')}

          {/* Action cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            {[
              { icon: 'edit_note', label: 'Edit Content', desc: 'Text, images, pages', action: 'edit' },
              { icon: 'palette', label: 'Change Design', desc: 'Colors, fonts, layout', action: 'design' },
              { icon: 'smart_toy', label: 'AI Help', desc: 'Chat with your assistant', action: 'chat' },
              { icon: 'analytics', label: 'View Analytics', desc: 'Visitors & traffic', action: 'analytics' },
            ].map((card) => (
              <button
                key={card.label}
                onClick={() => {
                  if (card.action === 'chat') setChatDrawerOpen(true);
                  else if ((card.action === 'edit' || card.action === 'design') && activeSite) {
                    transitionToStudio(activeSite);
                    setStudioTab(card.action === 'edit' ? 'content' : 'design');
                  }
                }}
                className="group p-4 bg-zinc-900/60 border border-zinc-800 rounded-xl hover:border-orange-500/40 hover:bg-orange-500/5 transition-all text-left"
              >
                <span className="material-icons text-2xl text-zinc-500 group-hover:text-orange-400 transition mb-2 block">{card.icon}</span>
                <p className="text-sm font-medium text-white">{card.label}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">{card.desc}</p>
              </button>
            ))}
          </div>

          {/* AI Suggestions */}
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 mt-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="material-icons text-orange-400 text-lg">auto_awesome</span>
              <h3 className="text-sm font-medium text-white">AI Suggestions</h3>
            </div>
            <div className="space-y-2">
              {suggestions.map((s) => (
                <div
                  key={s.id}
                  className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg transition ${
                    s.applied ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-zinc-800/40 border border-zinc-700/30 hover:border-zinc-600'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`material-icons text-sm ${s.applied ? 'text-emerald-400' : 'text-zinc-500'}`}>
                      {s.applied ? 'check_circle' : s.icon}
                    </span>
                    <span className={`text-sm ${s.applied ? 'text-emerald-300 line-through' : 'text-zinc-300'}`}>{s.text}</span>
                  </div>
                  {!s.applied && (
                    <button
                      onClick={() => setSuggestions((prev) => prev.map((x) => (x.id === s.id ? { ...x, applied: true } : x)))}
                      className="shrink-0 px-3 py-1 bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/30 rounded-md text-xs text-orange-300 transition"
                    >
                      Apply
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Your Plan + Perks */}
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="material-icons text-zinc-500 text-lg">verified</span>
                <h3 className="text-sm font-medium text-white">Your Plan</h3>
                {renderTierBadge(activeSite)}
              </div>
              {activeSite?.tier !== 'pro' && (
                <button
                  onClick={() => { setShowClaimOverlay(true); setClaimStep(activeSite?.tier === 'sandbox' ? 'choose' : 'domain'); }}
                  className="text-xs text-orange-400 hover:text-orange-300 transition"
                >
                  {activeSite?.tier === 'sandbox' ? 'Claim site' : 'Upgrade'}
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {PERKS.map((p) => (
                <div key={p.label} className="flex items-center gap-1.5 p-2 bg-zinc-800/30 rounded-lg">
                  <span className="material-icons text-orange-400/70 text-sm">{p.icon}</span>
                  <span className="text-[10px] text-zinc-400">{p.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Backups */}
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="material-icons text-zinc-500 text-lg">backup</span>
                <h3 className="text-sm font-medium text-white">Backups</h3>
              </div>
              <span className="text-[10px] text-zinc-600">Auto-saved every change</span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {Array.from({ length: Math.min(activeSite?.version || 1, 5) }, (_, i) => {
                const v = (activeSite?.version || 1) - i;
                const isLatest = i === 0;
                return (
                  <div key={v} className={`shrink-0 px-3 py-2 rounded-lg border text-xs cursor-pointer transition ${
                    isLatest ? 'bg-orange-500/10 border-orange-500/30 text-orange-300' : 'bg-zinc-800/40 border-zinc-700/40 text-zinc-400 hover:border-zinc-600'
                  }`}>
                    <p className="font-medium">v{v}{isLatest ? ' — Current' : ''}</p>
                    <p className="text-zinc-600 mt-0.5">{isLatest ? 'Just now' : `${i * 20}m ago`}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-4 p-3 bg-sky-500/5 border border-sky-500/15 rounded-lg flex items-start gap-2 md:hidden">
            <span className="material-icons text-sky-400 text-lg shrink-0">phone_iphone</span>
            <p className="text-xs text-sky-300/70">Content editing works great on mobile! For layout and design changes, switch to a desktop or laptop.</p>
          </div>

          <div className="mt-6 text-center pb-4">
            <button className="text-xs text-zinc-600 hover:text-zinc-400 transition">
              Switch to Advanced View <span className="material-icons text-xs align-middle">arrow_forward</span>
            </button>
          </div>
        </div>
      </div>

      {!chatDrawerOpen && (
        <button
          onClick={() => setChatDrawerOpen(true)}
          className="fixed bottom-6 right-6 w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center shadow-glow hover:shadow-glow-lg transition-all hover:-translate-y-0.5 z-40"
        >
          <span className="material-icons text-white text-lg">chat</span>
        </button>
      )}

      {chatDrawerOpen && (
        <div className="fixed bottom-6 right-6 w-[360px] max-w-[calc(100vw-3rem)] h-[460px] max-h-[calc(100vh-6rem)] bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800 shrink-0">
            <div className="flex items-center gap-2">
              <span className="material-icons text-orange-400 text-sm">smart_toy</span>
              <span className="text-sm font-medium text-white">AI Assistant</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { setChatDrawerOpen(false); if (activeSite) transitionToStudio(activeSite); }}
                className="text-zinc-500 hover:text-orange-400 transition p-0.5" title="Expand to Studio"
              >
                <span className="material-icons text-sm">open_in_full</span>
              </button>
              <button onClick={() => setChatDrawerOpen(false)} className="text-zinc-500 hover:text-zinc-300 transition p-0.5">
                <span className="material-icons text-sm">close</span>
              </button>
            </div>
          </div>
          {renderChat(true)}
        </div>
      )}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <SandboxContext.Provider value={sandboxDbRef.current}>
      <div className="h-screen bg-base flex overflow-hidden">
        {renderSiteRail()}

        {mode === 'concierge' && renderConcierge()}
        {mode === 'studio' && renderStudio()}
        {mode === 'canvas' && renderCanvas()}

        {renderClaimOverlay()}
      </div>
    </SandboxContext.Provider>
  );
}
